// Send Logs' outbox (docs/features/send-logs.md: kept until the Mac acknowledges it — never-lose): at most one bundle
// per Mac on this device's disk, from the tap until that Mac's `logsAck ok`; a new Send for the same Mac replaces it.
// Its own small IndexedDB database, `cody-sendlogs` (offline/store.js's stores are fixed, and its `outbox` is
// prompts, which its flush would try to send); on memory where there is none (a private window, node).
//
// A record: { mac, name, id, data (the compressed bundle, base64), bytes, sha256, made, state, reason, retryAt }.
// `state` as the view says it: kept · sending · waiting (tooSoon: `retryAt`) · refused (`reason`, the Mac's code).

const DB = 'cody-sendlogs'
const STORE = 'bundles'

let opening = null
function openDB(idb) {
  if (!idb) return Promise.resolve(null)
  opening ||= new Promise((resolve) => {
    let req
    try { req = idb.open(DB, 1) } catch { return resolve(null) }
    req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'mac' }) }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
    req.onblocked = () => resolve(null)
  })
  return opening
}

const done = (req) => new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result); req.onerror = () => reject(req.error) })
const copy = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)))

/// The same calls on memory: nothing kept across a reload.
export function memoryOutbox() {
  const rows = new Map()
  return {
    persistent: false,
    all: async () => [...rows.values()].map(copy),
    get: async (mac) => copy(rows.get(mac)),
    put: async (record) => { rows.set(record.mac, copy(record)) },
    del: async (mac) => { rows.delete(mac) },
  }
}

/// The outbox on this page's IndexedDB; a write IndexedDB refuses still lands in memory, never dropped.
export async function openOutbox({ idb = globalThis.indexedDB } = {}) {
  const db = await openDB(idb)
  const spare = memoryOutbox()
  if (!db) return spare
  const tx = (mode) => db.transaction(STORE, mode).objectStore(STORE)
  const guard = (work, fallback) => async (...args) => { try { return await work(...args) } catch { return fallback(...args) } }
  return {
    persistent: true,
    all: guard(async () => [...await done(tx('readonly').getAll()), ...await spare.all()], spare.all),
    get: guard(async (mac) => (await done(tx('readonly').get(mac))) ?? spare.get(mac), spare.get),
    put: guard((record) => done(tx('readwrite').put(record)), spare.put),
    del: guard(async (mac) => { await spare.del(mac); return done(tx('readwrite').delete(mac)) }, spare.del),
  }
}
